import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface ImpersonatedUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  role: 'superadmin' | 'owner' | 'admin' | 'manager' | 'employee';
  organization_id: string | null;
  avatar_url: string | null;
}

interface ImpersonationSession {
  id: string;
  admin_user_id: string;
  target_user_id: string;
  started_at: string;
  reason?: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  impersonationSession: ImpersonationSession | null;
  startImpersonation: (targetUserId: string, reason?: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  canImpersonate: (targetUserId: string) => Promise<boolean>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'blazepos-impersonation';

interface StoredImpersonation {
  sessionId: string;
  targetUserId: string;
  startedAt: string;
}

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [impersonationSession, setImpersonationSession] = useState<ImpersonationSession | null>(null);

  // Restore impersonation from storage on mount
  useEffect(() => {
    const restoreImpersonation = async () => {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (!stored) return;

      try {
        const data: StoredImpersonation = JSON.parse(stored);
        
        // Verify the session is still active
        const { data: session, error } = await supabase
          .from('impersonation_sessions')
          .select('*')
          .eq('id', data.sessionId)
          .is('ended_at', null)
          .single();

        if (error || !session) {
          // Session no longer valid, clear storage
          localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
          return;
        }

        // Fetch the impersonated user
        const { data: user } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.targetUserId)
          .single();

        if (user) {
          setImpersonatedUser(user);
          setImpersonationSession(session);
          setIsImpersonating(true);
        }
      } catch (error) {
        console.error('Failed to restore impersonation:', error);
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    };

    restoreImpersonation();
  }, []);

  const canImpersonate = useCallback(async (targetUserId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .rpc('can_impersonate', { p_target_user_id: targetUserId } as any);

    if (error) {
      console.error('Error checking impersonation permission:', error);
      return false;
    }

    return data === true;
  }, []);

  const startImpersonation = useCallback(async (targetUserId: string, reason?: string) => {
    // First check if allowed
    const canDo = await canImpersonate(targetUserId);
    if (!canDo) {
      throw new Error('You do not have permission to impersonate this user');
    }

    // Create impersonation session
    // Get current user ID for admin_user_id
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) throw new Error('Not authenticated');

    const { data: session, error: sessionError } = await supabase
      .from('impersonation_sessions')
      .insert({
        admin_user_id: currentUser.id,
        target_user_id: targetUserId,
        reason,
      } as any)
      .select()
      .single();

    if (sessionError) throw sessionError;

    // Fetch target user profile
    const { data: user, error: userError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', targetUserId)
      .single();

    if (userError) throw userError;

    // Log the impersonation start in audit logs
    await supabase.from('audit_logs').insert({
      action: 'impersonate_start',
      entity_type: 'impersonation',
      entity_id: (session as any).id,
      new_values: {
        target_user_id: targetUserId,
        target_user_name: `${(user as any).first_name} ${(user as any).last_name}`,
        reason,
      },
    } as any);

    // Store in localStorage for persistence
    const storageData: StoredImpersonation = {
      sessionId: (session as any).id,
      targetUserId,
      startedAt: (session as any).started_at,
    };
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(storageData));

    setImpersonatedUser(user as any);
    setImpersonationSession(session as any);
    setIsImpersonating(true);
  }, [canImpersonate]);

  const endImpersonation = useCallback(async () => {
    if (!impersonationSession) return;

    // Update the session end time
    await (supabase
      .from('impersonation_sessions') as any)
      .update({ ended_at: new Date().toISOString() })
      .eq('id', impersonationSession.id);

    // Log the impersonation end
    await supabase.from('audit_logs').insert({
      action: 'impersonate_end',
      entity_type: 'impersonation',
      entity_id: impersonationSession.id,
      old_values: {
        target_user_id: impersonatedUser?.id,
        target_user_name: impersonatedUser 
          ? `${impersonatedUser.first_name} ${impersonatedUser.last_name}` 
          : null,
        duration_seconds: Math.floor(
          (Date.now() - new Date(impersonationSession.started_at).getTime()) / 1000
        ),
      },
    } as any);

    // Clear storage and state
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setImpersonationSession(null);
  }, [impersonationSession, impersonatedUser]);

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        impersonatedUser,
        impersonationSession,
        startImpersonation,
        endImpersonation,
        canImpersonate,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}

